// src/pages/JobTracker.jsx
import React, { useState, useEffect } from 'react';
import { Container, Row, Col, Card, Form, Button, Spinner, Alert, Table, Badge, Modal } from 'react-bootstrap';
import { useAuth } from '../contexts/AuthContext';
import { supabase } from '../utils/supabaseClient';
import { Briefcase, Plus, Edit, Trash2 } from 'lucide-react';

const JobTracker = () => {
  const { user, loading: authLoading } = useAuth();
  const [applications, setApplications] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [editingApp, setEditingApp] = useState(null);
  const [filter, setFilter] = useState('all');

  // Form state
  const [formData, setFormData] = useState({
    company: '',
    position: '',
    status: 'Applied',
    date_applied: new Date().toISOString().split('T')[0],
    job_url: '',
    notes: ''
  });

  const statusOptions = ['Applied', 'Phone Screen', 'Interview', 'Technical Test', 'Final Interview', 'Offer', 'Rejected', 'Accepted'];

  // Fetch applications when user is available
  useEffect(() => {
    if (user) {
      console.log('✅ User found, fetching applications...');
      fetchApplications();
    } else if (!authLoading) {
      console.log('ℹ️ No user found, auth loading complete');
      setLoading(false);
    }
  }, [user, authLoading]);

  const fetchApplications = async () => {
    try {
      setLoading(true);
      setError(null);

      const { data, error } = await supabase
        .from('job_applications')
        .select('*')
        .eq('user_id', user.id)
        .order('date_applied', { ascending: false });

      if (error) {
        console.error('❌ Fetch error:', error);
        if (error.code === '42501') {
          throw new Error('Permission denied. Please check RLS policies for the job_applications table.');
        }
        if (error.code === '42P01') {
          throw new Error('The job_applications table does not exist. Please run the database setup SQL.');
        }
        throw error;
      }

      setApplications(data || []);
    } catch (err) {
      console.error('❌ Fetch error:', err);
      setError(err.message || 'Failed to load applications');
      setApplications([]);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      setError(null);

      const dataToSave = { ...formData, user_id: user.id };

      let result;
      if (editingApp) {
        result = await supabase
          .from('job_applications')
          .update(dataToSave)
          .eq('id', editingApp.id);
      } else {
        result = await supabase
          .from('job_applications')
          .insert([dataToSave]);
      }

      if (result.error) throw result.error;

      await fetchApplications();
      handleCloseModal();
    } catch (err) {
      console.error('❌ Save error:', err);
      setError(err.message);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this application?')) return;

    try {
      setError(null);
      const { error } = await supabase
        .from('job_applications')
        .delete()
        .eq('id', id);

      if (error) throw error;
      await fetchApplications();
    } catch (err) {
      console.error('❌ Delete error:', err);
      setError(err.message);
    }
  };

  const handleEdit = (app) => {
    setEditingApp(app);
    setFormData({
      company: app.company,
      position: app.position,
      status: app.status,
      date_applied: app.date_applied,
      job_url: app.job_url || '',
      notes: app.notes || ''
    });
    setShowModal(true);
  };

  const handleCloseModal = () => {
    setShowModal(false);
    setEditingApp(null);
    setFormData({
      company: '',
      position: '',
      status: 'Applied',
      date_applied: new Date().toISOString().split('T')[0],
      job_url: '',
      notes: ''
    });
  };

  const getStatusBadge = (status) => {
    const variants = {
      'Applied': 'secondary',
      'Phone Screen': 'info',
      'Interview': 'primary',
      'Technical Test': 'warning',
      'Final Interview': 'warning',
      'Offer': 'success',
      'Rejected': 'danger',
      'Accepted': 'success'
    };
    return variants[status] || 'secondary';
  };

  const filteredApplications = filter === 'all'
    ? applications
    : applications.filter(app => app.status === filter);

  const stats = {
    total: applications.length,
    applied: applications.filter(a => a.status === 'Applied').length,
    interviewing: applications.filter(a => ['Phone Screen', 'Interview', 'Technical Test', 'Final Interview'].includes(a.status)).length,
    offered: applications.filter(a => a.status === 'Offer').length,
    accepted: applications.filter(a => a.status === 'Accepted').length,
    rejected: applications.filter(a => a.status === 'Rejected').length
  };

  // Loading (auth)
  if (authLoading) {
    return (
      <div className="d-flex justify-content-center align-items-center" style={{ height: '80vh' }}>
        <div className="text-center">
          <Spinner animation="border" style={{ color: '#1A9E96' }} />
          <p className="mt-3">Loading...</p>
        </div>
      </div>
    );
  }

  // No user
  if (!user) {
    return (
      <div className="py-4" style={{ paddingTop: '80px' }}>
        <Container>
          <Alert variant="warning">
            <strong>Please login</strong> to view and manage your job applications.
          </Alert>
        </Container>
      </div>
    );
  }

  // Error
  if (error) {
    return (
      <div className="py-4" style={{ paddingTop: '80px' }}>
        <Container>
          <Alert variant="danger">
            <strong>Error loading applications:</strong> {error}
            <Button
              variant="outline-danger"
              size="sm"
              className="ms-3"
              onClick={fetchApplications}
            >
              Retry
            </Button>
          </Alert>
        </Container>
      </div>
    );
  }

  // Loading (data)
  if (loading) {
    return (
      <div className="d-flex justify-content-center align-items-center" style={{ height: '80vh' }}>
        <div className="text-center">
          <Spinner animation="border" style={{ color: '#1A9E96' }} />
          <p className="mt-3">Loading your applications...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="py-4" style={{ paddingTop: '80px' }}>
      <Container>
        {/* ===== Header row ===== */}
        <div className="d-flex flex-wrap justify-content-between align-items-center gap-3 mb-4">
          <div className="d-flex align-items-center gap-3">
            <Briefcase size={32} className="text-brand" />
            <h1 className="display-6 fw-bold mb-0">Job Tracker</h1>
            <span className="badge-active-header"></span>
          </div>

          <Button
            onClick={() => setShowModal(true)}
            className="btn-hero-primary d-flex align-items-center gap-2"
          >
            <Plus size={18} />
            Add Application
          </Button>
        </div>

        {/* ===== Stats Row ===== */}
        <Row className="g-3 mb-4">
          <Col xs={6} md={2}>
            <Card className="text-center">
              <Card.Body>
                <h5 className="stat-number">{stats.total}</h5>
                <small className="stat-label">Total</small>
              </Card.Body>
            </Card>
          </Col>
          <Col xs={6} md={2}>
            <Card className="text-center">
              <Card.Body>
                <h5 className="stat-number">{stats.applied}</h5>
                <small className="stat-label">Applied</small>
              </Card.Body>
            </Card>
          </Col>
          <Col xs={6} md={2}>
            <Card className="text-center">
              <Card.Body>
                <h5 className="stat-number">{stats.interviewing}</h5>
                <small className="stat-label">Interviewing</small>
              </Card.Body>
            </Card>
          </Col>
          <Col xs={6} md={2}>
            <Card className="text-center">
              <Card.Body>
                <h5 className="stat-number">{stats.offered}</h5>
                <small className="stat-label">Offers</small>
              </Card.Body>
            </Card>
          </Col>
          <Col xs={6} md={2}>
            <Card className="text-center">
              <Card.Body>
                <h5 className="stat-number">{stats.accepted}</h5>
                <small className="stat-label">Accepted</small>
              </Card.Body>
            </Card>
          </Col>
          <Col xs={6} md={2}>
            <Card className="text-center">
              <Card.Body>
                <h5 className="stat-number">{stats.rejected}</h5>
                <small className="stat-label">Rejected</small>
              </Card.Body>
            </Card>
          </Col>
        </Row>

        {/* ===== Filter ===== */}
        <div className="d-flex justify-content-end mb-3">
          <Form.Select
            style={{ width: '220px' }}
            value={filter}
            onChange={(e) => setFilter(e.target.value)}
          >
            <option value="all">All Applications</option>
            {statusOptions.map(status => (
              <option key={status} value={status}>{status}</option>
            ))}
          </Form.Select>
        </div>

        {/* ===== Applications Table ===== */}
        <Card className="shadow-sm">
          <Card.Body>
            {filteredApplications.length === 0 ? (
              <div className="text-center py-5 text-muted">
                <Briefcase size={48} className="mb-3 opacity-25" />
                <p>No job applications yet.</p>
                <Button
                  size="sm"
                  onClick={() => setShowModal(true)}
                  className="btn-hero-primary"
                >
                  Add Your First Application
                </Button>
              </div>
            ) : (
              <div className="table-responsive">
                <Table hover>
                  <thead>
                    <tr>
                      <th>Company</th>
                      <th>Position</th>
                      <th>Status</th>
                      <th>Date Applied</th>
                      <th>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredApplications.map((app) => (
                      <tr key={app.id}>
                        <td className="fw-bold">{app.company}</td>
                        <td>{app.position}</td>
                        <td>
                          <Badge bg={getStatusBadge(app.status)}>
                            {app.status}
                          </Badge>
                        </td>
                        <td>{new Date(app.date_applied).toLocaleDateString()}</td>
                        <td>
                          <Button
                            variant="outline-primary"
                            size="sm"
                            onClick={() => handleEdit(app)}
                            className="me-1"
                          >
                            <Edit size={14} />
                          </Button>
                          <Button
                            variant="outline-danger"
                            size="sm"
                            onClick={() => handleDelete(app.id)}
                          >
                            <Trash2 size={14} />
                          </Button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </Table>
              </div>
            )}
          </Card.Body>
        </Card>

        {/* ===== Add/Edit Modal ===== */}
        <Modal show={showModal} onHide={handleCloseModal}>
          <Modal.Header closeButton>
            <Modal.Title>{editingApp ? 'Edit Application' : 'Add Job Application'}</Modal.Title>
          </Modal.Header>
          <Form onSubmit={handleSubmit}>
            <Modal.Body>
              <Form.Group className="mb-3">
                <Form.Label>Company *</Form.Label>
                <Form.Control
                  type="text"
                  required
                  value={formData.company}
                  onChange={(e) => setFormData({ ...formData, company: e.target.value })}
                  placeholder="e.g., Google"
                />
              </Form.Group>

              <Form.Group className="mb-3">
                <Form.Label>Position *</Form.Label>
                <Form.Control
                  type="text"
                  required
                  value={formData.position}
                  onChange={(e) => setFormData({ ...formData, position: e.target.value })}
                  placeholder="e.g., Software Engineer"
                />
              </Form.Group>

              <Form.Group className="mb-3">
                <Form.Label>Status</Form.Label>
                <Form.Select
                  value={formData.status}
                  onChange={(e) => setFormData({ ...formData, status: e.target.value })}
                >
                  {statusOptions.map(status => (
                    <option key={status} value={status}>{status}</option>
                  ))}
                </Form.Select>
              </Form.Group>

              <Form.Group className="mb-3">
                <Form.Label>Date Applied</Form.Label>
                <Form.Control
                  type="date"
                  value={formData.date_applied}
                  onChange={(e) => setFormData({ ...formData, date_applied: e.target.value })}
                />
              </Form.Group>

              <Form.Group className="mb-3">
                <Form.Label>Job URL</Form.Label>
                <Form.Control
                  type="url"
                  value={formData.job_url}
                  onChange={(e) => setFormData({ ...formData, job_url: e.target.value })}
                  placeholder="https://..."
                />
              </Form.Group>

              <Form.Group className="mb-3">
                <Form.Label>Notes</Form.Label>
                <Form.Control
                  as="textarea"
                  rows={3}
                  value={formData.notes}
                  onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                  placeholder="Any notes about this application..."
                />
              </Form.Group>
            </Modal.Body>
            <Modal.Footer>
              <Button variant="secondary" onClick={handleCloseModal}>
                Cancel
              </Button>
              <Button type="submit" className="btn-hero-primary">
                {editingApp ? 'Update' : 'Save'} Application
              </Button>
            </Modal.Footer>
          </Form>
        </Modal>
      </Container>
    </div>
  );
};

export default JobTracker;